import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/styles/ui/tooltip";
import { TooltipArrow } from "@radix-ui/react-tooltip";
import React, {
  cloneElement,
  forwardRef,
  isValidElement,
  useEffect,
  useState,
} from "react";

export type TooltipWrapperProps = {
  label: string | React.ReactNode;
  arrowed?: boolean;
  forceClose?: boolean;
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  align?: "start" | "center" | "end";
  alignOffset?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
  styles?: {
    contentStyle?: string;
    bgStyle?: string;
    textStyle?: string;
    arrowStyle?: string;
  };
};

export const TooltipWrapper = forwardRef<HTMLElement, TooltipWrapperProps>(
  (
    {
      label,
      arrowed = false,
      forceClose = false,
      defaultOpen = false,
      side = "top",
      sideOffset,
      align = "center",
      alignOffset,
      styles,
      children,
      ...props
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    useEffect(() => {
      if (forceClose) {
        setIsOpen(false);
      }
    }, [forceClose]);
    return (
      <TooltipProvider>
        <Tooltip
          open={isOpen}
          onOpenChange={setIsOpen}
          defaultOpen={defaultOpen}
        >
          <TooltipTrigger asChild>
            {typeof children === "object" && isValidElement(children)
              ? cloneElement(children as React.ReactElement<any>, {
                  ref,
                  ...props,
                })
              : children}
          </TooltipTrigger>
          <TooltipContent
            className={cn(
              " bg-white border border-neutral-200",
              styles?.contentStyle,
            )}
            side={side}
            sideOffset={sideOffset}
            align={align}
            alignOffset={alignOffset}
          >
            <div
              className={cn(
                "flex items-start w-full h-full gap-2",
                styles?.bgStyle,
              )}
            >
              {typeof label === "string" ? (
                <p
                  className={cn(
                    "caption-2-regular text-primary",
                    styles?.textStyle,
                  )}
                >
                  {label}
                </p>
              ) : (
                label
              )}
            </div>
            {arrowed && <TooltipArrow className={styles?.arrowStyle} />}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);

TooltipWrapper.displayName = "TooltipWrapper";

export const ArrowedButtonTooltip = (props: TooltipWrapperProps) => {
  return (
    <TooltipWrapper
      arrowed={true}
      {...props}
      styles={{
        contentStyle: cn("border-none p-0", props.styles?.contentStyle),
        textStyle: cn("caption-2-regular text-white", props.styles?.textStyle),
        bgStyle: cn(
          " backdrop-blur-sm bg-black/[.43] px-2.5 py-2",
          props.styles?.bgStyle,
        ),
        arrowStyle: cn("fill-black opacity-[.43]", props.styles?.arrowStyle),
      }}
    />
  );
};
